import { FilterNbAirport, SearchResponse } from '@/src/types/flight'
import { MulticitySearchBody } from '@/src/types/searchQuery'
import { encryptQueryString } from '@/src/utils/crypto/encryption'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'
import { DEFAULT_WEIGHTS } from '@/src/utils/srp/aiRecommendation/metrics'
import { buildMulticityFilterData } from '@/src/utils/srp/multicity/buildMulticityFilterData'
import {
    airlineFilterPriceModifier,
    priceRangeFilterPriceModifier,
    stopsFilterPriceModifier
} from '@/src/utils/srp/RT/filter-price-modier'
import { modifyPricesAndSortOnwardLeg } from '@/src/utils/srp/RT/sort-modifier'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

interface SrpResultsArgs {
    query: string
}

const buildDefaultSmartRecommendation = () => ({
    suggestedFilters: null,
    weights: DEFAULT_WEIGHTS,
    preferences: null,
    priority: null,
    sortPreference: null
})

const srpFlightResultsApi = createApi({
    reducerPath: 'srpFlightResultsApi',
    baseQuery: fetchBaseQuery({
        prepareHeaders: headers => {
            headers.set('x-time-zone', Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone)
            return setCommonHeaders(headers)
        }
    }),
    endpoints: builder => ({
        getSrpResults: builder.query<SearchResponse, SrpResultsArgs>({
            query: ({ query }) => {
                const encryptedData = encryptQueryString(query)
                return {
                    url: `/flight/search/v2/results?data=${encryptedData}`,
                    headers: {
                        'x-multi-airline': 'true'
                    }
                }
            },
            transformResponse: (response: SearchResponse, _meta, arg) => {
                if (!response) return response
                response.smartRecommendationData = [
                    buildDefaultSmartRecommendation(),
                    buildDefaultSmartRecommendation()
                ]

                const isRT = response?.cards?.length === 2
                response.solutionFlowSelectedLeg = 0

                const rawNbFilter = (response.filterData?.nbAirportFilter ?? []) as unknown as FilterNbAirport[]
                const searchParams = new URLSearchParams(arg?.query || '')
                const searchFrom = searchParams.get('from')?.toUpperCase() || ''
                const searchTo = searchParams.get('to')?.toUpperCase() || ''
                const deepCopyGroups = (groups: FilterNbAirport[]): FilterNbAirport[] =>
                    groups.map(g => ({ ...g, options: [...g.options], selectedValues: [...g.selectedValues] }))
                const buildEmptyGroup = (airportCode: string): FilterNbAirport => ({
                    airportCode,
                    options: [],
                    selectedValues: []
                })
                const normalizeNbGroups = (
                    groups: FilterNbAirport[],
                    departureCode: string,
                    arrivalCode: string
                ): FilterNbAirport[] => {
                    const copied = deepCopyGroups(groups)
                    if (!departureCode || !arrivalCode) return copied

                    const byCode = new Map(copied.map(group => [group.airportCode?.toUpperCase(), group]))
                    const fallbackGroups = copied.filter(
                        group => ![departureCode, arrivalCode].includes(group.airportCode?.toUpperCase())
                    )

                    const departureGroup = byCode.get(departureCode) || fallbackGroups[0] || buildEmptyGroup(departureCode)
                    const arrivalGroup = byCode.get(arrivalCode) || fallbackGroups[1] || buildEmptyGroup(arrivalCode)

                    return [departureGroup, arrivalGroup]
                }

                response.filterData.nbAirportFilter = [normalizeNbGroups(rawNbFilter, searchFrom, searchTo)]
                if (isRT) {
                    response.filterData.nbAirportFilter[1] = normalizeNbGroups(rawNbFilter, searchTo, searchFrom)
                    modifyPricesAndSortOnwardLeg(response)
                    // In split view ALL onward price surfaces use the individual leg fare (splitLegFare):
                    // stops/airline filter labels AND the price-range slider bounds — matching filterCards'
                    // price-range inclusion (activePriceField) and the leg cards, so nothing is ever mixed.
                    // NOTE: this is read from the module flag (not the query arg) on purpose — adding it to
                    // the arg would change the RTK-Query cache key and desync it from every updateQueryData
                    // patcher (which key on { query }), breaking the leg-switch/return-reprice cache writes.
                    const RT_SPLIT_VIEW_ENABLED = searchParams.get('rtsplitview') === 'true'
                    const priceField = RT_SPLIT_VIEW_ENABLED ? 'splitLegFare' : 'multiAirlinePrice'
                    stopsFilterPriceModifier(response, 0, priceField)
                    airlineFilterPriceModifier(response, 0, priceField)
                    priceRangeFilterPriceModifier(response, 0, priceField)
                }

                return response
            }
        }),
        getMulticityResults: builder.query<SearchResponse, MulticitySearchBody>({
            query: body => ({
                url: '/flight/search/v2/multicity/results',
                method: 'POST',
                body,
                headers: {
                    'x-multi-airline': 'false'
                }
            }),
            transformResponse: (response: SearchResponse, _meta, body: MulticitySearchBody) => {
                if (!response) return response

                // Multicity is a single-list (leg 0) flow; no RT pairing/solution stepping.
                response.smartRecommendationData = [buildDefaultSmartRecommendation()]
                response.solutionFlowSelectedLeg = 0
                response.isMulticity = true

                const cards = response.cards?.[0] ?? []
                const { filterData, journeyCount } = buildMulticityFilterData({
                    cards,
                    sectors: response.sectors ?? {},
                    searchSectors: body?.sectors ?? [],
                    airlineNames: response.jsons?.airline_names ?? {}
                })

                response.filterData = filterData
                response.journeyCount = journeyCount
                if (response.jsons.userInfo?.usrId) response.jsons.userInfo.usrId = '65233884'
                return response
            }
        })
    })
})

export const {
    useGetSrpResultsQuery,
    useLazyGetSrpResultsQuery,
    useGetMulticityResultsQuery,
    useLazyGetMulticityResultsQuery
} = srpFlightResultsApi
export default srpFlightResultsApi
